import { AuditableModel } from '../../Misc/models/auditable.model';
import { LeftSidebarListItemConvertableModel } from '../../Layout/models/left-sidebar-list-item-convertable.model';
import { LeftSidebarListItemButton } from '../../Layout/models/left-sidebar-list-item-button.model';
import { LeftSidebarListItem } from '../../Layout/models/left-sidebar-list-item.model';
import { DistributionStatus } from '../../TargetingShared/enum/distribution-status.enum';
import { DistributionSummaryApiModel } from '../../../api/distributions/model/distribution-summary-api.model';
import { ActionsEnum } from '../../Basic/enums/actions.enum';
import { DistributionType } from '../enum/distribution-type.enum';
import { DistributionSendFrom } from '../../../Discover/Targeting/models/distribution-send-from.model';
import { BaseModel } from '../../../Common/Basic/models/base.model';

export class DistributionSummaryModel extends BaseModel<DistributionSummaryModel, DistributionSummaryApiModel>
    implements AuditableModel, LeftSidebarListItemConvertableModel {

    public static readonly distributionDashboardId = -1;

    id?: number;
    name?: string;

    status: DistributionStatus;
    type?: DistributionType;
    individualRecipientsListId?: number;
    sendFrom: DistributionSendFrom;

    hidden?: boolean;
    groupId: number;
    isFavorite: boolean;

    // F.E only
    icon?: string;
    disabled?: boolean;
    tooltip?: string;

    // readonly
    createdByUserId?: number;
    createdDate?: Date;

    modifiedByUserId?: number;
    modifiedDate?: Date;

    constructor(apiModel?: DistributionSummaryApiModel) {
        super();

        if (apiModel) {
            this.constructFromApiModel(apiModel);
        } else {
            this.constructDefault();
        }
    }

    constructFromApiModel(apiModel: DistributionSummaryApiModel) {
        this.id = +apiModel.id;
        this.name = apiModel.name;

        this.status = <DistributionStatus> apiModel.status;
        this.type = apiModel.type;
        this.individualRecipientsListId = apiModel.individualRecipientsListId;

        if (apiModel.sendFrom) {
            this.sendFrom = new DistributionSendFrom(apiModel.sendFrom);
        }

        this.hidden = apiModel.hidden;
        this.groupId = apiModel.displayGroupId;
        this.isFavorite = apiModel.isFavorited;

        this.createdByUserId = apiModel.createdUserId;
        this.createdDate = new Date(apiModel.createdDate);

        this.modifiedByUserId = apiModel.modifiedUserId;
        this.modifiedDate = apiModel.modifiedDate ? new Date(apiModel.modifiedDate) : null;
    }

    constructDefault() {
        this.isFavorite = false;
    }


    toApiModel(): DistributionSummaryApiModel {
        const apiModel: DistributionSummaryApiModel = {
            id: this.id,
            name: this.name,
            status: this.status,
            type: this.type,
            individualRecipientsListId: this.individualRecipientsListId,
            sendFrom: this.sendFrom?.toApiModel(),
            isFavorited: this.isFavorite,
            displayGroupId: this.groupId,
            hidden: this.hidden,
            createdUserId: this.createdByUserId,
            createdDate: this.createdDate,
            modifiedUserId: this.modifiedByUserId,
            modifiedDate: this.modifiedDate
        };

        return apiModel;
    }


    clone(): DistributionSummaryModel {
        const clone = new DistributionSummaryModel(this.toApiModel());
        clone.icon = this.icon;
        clone.disabled = this.disabled;
        clone.tooltip = this.tooltip;

        return clone;
    }


    equals(other?: DistributionSummaryModel): boolean {
        if (!other) {
            return false;
        }

        if (this.id !== other.id) {
            return false;
        }

        if (this.name !== other.name) {
            return false;
        }

        if (this.status !== other.status) {
            return false;
        }

        if (this.type !== other.type) {
            return false;
        }

        if (this.individualRecipientsListId !== other.individualRecipientsListId) {
            return false;
        }

        if (!this.sendFrom.equals(other.sendFrom)) {
            return false;
        }

        if (this.hidden !== other.hidden) {
            return false;
        }

        if (this.groupId !== other.groupId) {
            return false;
        }

        if (this.isFavorite !== other.isFavorite) {
            return false;
        }

        if (this.icon !== other.icon) {
            return false;
        }

        if (this.disabled !== other.disabled) {
            return false;
        }

        if (this.tooltip !== other.tooltip) {
            return false;
        }

        return true;
    }


    toLeftSidebarListItem(buttons?: LeftSidebarListItemButton[]): LeftSidebarListItem {
        if (buttons){
            if (this.hidden) {
                buttons = buttons.filter(button => button.key !== ActionsEnum.hide);
            } else {
                buttons = buttons.filter(button => button.key !== ActionsEnum.show);
            }

            if (this.isFavorite) {
                buttons = buttons.filter(button => button.key !== ActionsEnum.favorite);
            } else {
                buttons = buttons.filter(button => button.key !== ActionsEnum.unfavorite);
            }
        }

        return {
            label : this.name,
            value : this.id,
            buttons: buttons,
            status: this.status,
            groupId: this.groupId,
            isFavorite: this.isFavorite,
            icon: this.icon,
            disabled: this.disabled,
            tooltip: this.tooltip
        };
    }
}
